import { useState } from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { Check, ArrowRight } from 'lucide-react'
import PricingCard from '../components/PricingCard'
import CTASection from '../components/CTASection'

const PageHeader = styled.section`
  padding: calc(var(--header-height) + 64px) 24px 64px;
  text-align: center;
  position: relative;
`

const HeaderGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 0%, var(--color-accent-glow) 0%, transparent 60%);
  pointer-events: none;
`

const HeaderContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 700px;
  margin: 0 auto;
`

const Eyebrow = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 16px;
`

const PageTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
  line-height: 1.15;
`

const PageSubtitle = styled.p`
  font-size: 1.15rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
`

const PricingSection = styled.section`
  padding: 32px 24px 96px;
`

const PricingInner = styled.div`
  max-width: 1060px;
  margin: 0 auto;
`

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
`

const WaitlistSection = styled.section`
  padding: 64px 24px 96px;
`

const WaitlistInner = styled.div`
  max-width: 560px;
  margin: 0 auto;
  text-align: center;
`

const WaitlistCard = styled(motion.div)`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 40px 32px;
`

const WaitlistTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 12px;
`

const WaitlistDesc = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
  margin-bottom: 24px;
`

const EmailForm = styled.form`
  display: flex;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`

const EmailInput = styled.input`
  flex: 1;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--color-accent);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`

const SubmitButton = styled.button`
  padding: 14px 24px;
  background: linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end));
  color: white;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`

const SuccessMsg = styled(motion.p)`
  color: var(--color-success);
  font-weight: 500;
  font-size: 0.95rem;
`

const ComparisonSection = styled.section`
  padding: 0 24px 96px;
`

const ComparisonInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`

const ComparisonTitle = styled.h2`
  font-size: clamp(1.6rem, 2.5vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  text-align: center;
  margin-bottom: 40px;
`

const Table = styled.div`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  overflow: hidden;
`

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
  }
`

const TableHeader = styled(TableRow)`
  background: var(--color-bg-elevated);
`

const TableCell = styled.div`
  padding: 14px 20px;
  font-size: 0.9rem;
  color: ${({ $header }) => ($header ? 'var(--color-text)' : 'var(--color-text-secondary)')};
  font-weight: ${({ $header }) => ($header ? '600' : '400')};
  display: flex;
  align-items: center;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};

  svg {
    color: var(--color-checkmark);
  }

  @media (max-width: 600px) {
    padding: 10px 12px;
    font-size: 0.8rem;
  }
`

const CustomPlanBar = styled(motion.div)`
  margin-top: 24px;
  padding: 28px 32px;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`

const CustomPlanInfo = styled.div`
  flex: 1;
`

const CustomPlanName = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 6px;
`

const CustomPlanDesc = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
`

const CustomPlanButton = styled.a`
  padding: 14px 28px;
  background: linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end));
  color: white;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  white-space: nowrap;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
    color: white;
  }
`

const PLANS = [
  {
    name: 'Support Agent',
    price: '$300–$800',
    period: '/mo',
    description: 'FAQ handling, Tier 1 deflection, and intelligent routing. Your always-on front line.',
    features: [
      'AI chat widget for your site',
      'Custom knowledge base',
      'Business-specific tone of voice',
      'Multilingual support',
      'Custom branding',
      'Analytics dashboard',
    ],
    featured: false,
    buttonText: 'Join Waitlist',
  },
  {
    name: 'Lead & Intake Agent',
    price: '$500–$1,500',
    period: '/mo',
    description: 'Qualifies buyers, captures leads, and books appointments 24/7. Built for appointment-driven businesses.',
    features: [
      'Everything in Support',
      'Buyer/client qualification',
      'Appointment booking',
      'CRM integration',
      'Lead capture & follow-up',
      'Discovery question flows',
    ],
    featured: true,
    badge: 'Most Popular',
    buttonText: 'Join Waitlist',
  },
  {
    name: 'Coaching Agent',
    price: '$800–$2,000',
    period: '/mo',
    description: 'Extends your practice between sessions. Reflective questioning, goal tracking, and accountability in your voice.',
    features: [
      'Everything in Lead & Intake',
      'Custom discovery flows',
      'Goal tracking & check-ins',
      'Between-session coaching',
      'Voice AI (optional)',
      'Client progress analytics',
    ],
    featured: false,
    buttonText: 'Join Waitlist',
  },
]

const CUSTOM_PLAN = {
  name: 'Custom Agent',
  price: 'Contact Us',
  description: 'Multi-capability agents with custom integrations, voice AI, and enterprise features. We design and build around your needs.',
  features: [
    'Everything in Coaching',
    'Multi-agent routing',
    'Custom integrations',
    'Dedicated support & SLA',
    'Social content automation',
    'White-label options',
  ],
}

const COMPARISON = [
  { feature: 'AI Chat Widget', support: true, lead: true, coaching: true, custom: true },
  { feature: 'Knowledge Base', support: true, lead: true, coaching: true, custom: true },
  { feature: 'Custom Branding', support: true, lead: true, coaching: true, custom: true },
  { feature: 'Multilingual', support: true, lead: true, coaching: true, custom: true },
  { feature: 'Analytics Dashboard', support: true, lead: true, coaching: true, custom: true },
  { feature: 'Lead Qualification', support: false, lead: true, coaching: true, custom: true },
  { feature: 'Appointment Booking', support: false, lead: true, coaching: true, custom: true },
  { feature: 'CRM Integration', support: false, lead: true, coaching: true, custom: true },
  { feature: 'Discovery Flows', support: false, lead: false, coaching: true, custom: true },
  { feature: 'Goal Tracking', support: false, lead: false, coaching: true, custom: true },
  { feature: 'Voice AI', support: false, lead: false, coaching: true, custom: true },
  { feature: 'Multi-Agent Routing', support: false, lead: false, coaching: false, custom: true },
  { feature: 'Custom Integrations', support: false, lead: false, coaching: false, custom: true },
  { feature: 'Dedicated SLA', support: false, lead: false, coaching: false, custom: true },
]

export default function Platform() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (email) {
      setSubmitted(true)
      setEmail('')
    }
  }

  return (
    <>
      <PageHeader>
        <HeaderGlow />
        <HeaderContent>
          <Eyebrow>Agent Plans</Eyebrow>
          <PageTitle>Pick the agent that fits your business</PageTitle>
          <PageSubtitle>
            Every agent is configured for your brand, embedded on your site, and managed
            through your dashboard. No developer needed.
          </PageSubtitle>
        </HeaderContent>
      </PageHeader>

      <PricingSection>
        <PricingInner>
          <PricingGrid>
            {PLANS.map((plan, i) => (
              <PricingCard key={i} {...plan} delay={i * 0.1} />
            ))}
          </PricingGrid>
          <CustomPlanBar
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <CustomPlanInfo>
              <CustomPlanName>{CUSTOM_PLAN.name}</CustomPlanName>
              <CustomPlanDesc>{CUSTOM_PLAN.description}</CustomPlanDesc>
            </CustomPlanInfo>
            <CustomPlanButton href="https://calendly.com/coybyron/30min" target="_blank" rel="noopener noreferrer">
              Book a Call
            </CustomPlanButton>
          </CustomPlanBar>
        </PricingInner>
      </PricingSection>

      <WaitlistSection>
        <WaitlistInner>
          <WaitlistCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <WaitlistTitle>Coming Soon</WaitlistTitle>
            <WaitlistDesc>
              The self-serve platform is launching soon. Join the waitlist to get early access
              and a discount on your first month.
            </WaitlistDesc>
            {submitted ? (
              <SuccessMsg
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                You're on the list. We'll be in touch.
              </SuccessMsg>
            ) : (
              <EmailForm onSubmit={handleSubmit}>
                <EmailInput
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <SubmitButton type="submit">
                  Join Waitlist <ArrowRight size={16} />
                </SubmitButton>
              </EmailForm>
            )}
          </WaitlistCard>
        </WaitlistInner>
      </WaitlistSection>

      <ComparisonSection>
        <ComparisonInner>
          <ComparisonTitle>Feature comparison</ComparisonTitle>
          <Table>
            <TableHeader>
              <TableCell $header>Feature</TableCell>
              <TableCell $header $center>Support</TableCell>
              <TableCell $header $center>Lead</TableCell>
              <TableCell $header $center>Coaching</TableCell>
              <TableCell $header $center>Custom</TableCell>
            </TableHeader>
            {COMPARISON.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{row.feature}</TableCell>
                <TableCell $center>{row.support ? <Check size={16} /> : '—'}</TableCell>
                <TableCell $center>{row.lead ? <Check size={16} /> : '—'}</TableCell>
                <TableCell $center>{row.coaching ? <Check size={16} /> : '—'}</TableCell>
                <TableCell $center>{row.custom ? <Check size={16} /> : '—'}</TableCell>
              </TableRow>
            ))}
          </Table>
        </ComparisonInner>
      </ComparisonSection>

      <CTASection
        title="Prefer a human touch?"
        subtitle="Our consulting team configures everything for you. Same platform, hands-on implementation."
        buttonText="Book a Discovery Call"
        buttonTo="/contact"
      />
    </>
  )
}
